import { Button, Buttons, Flex, Icon, Image, Link, Ribbon, Text } from '@bitrise/bitkit';
import React, { FunctionComponent } from 'react';

import dataAtIds from '../../../../../../dataAtIds.json';
import useDownloadConfigAndroid from '../../../../../Settings/useDownloadConfigAndroid';
import Guide from '../../Guide';
import GuideCode from '../../GuideCode';
import GuideStep from '../../GuideStep';
import Step2Part3Image from './Step2-3.png';
import Step3Part2Image from './Step3-2.png';
import Step3Part4Image from './Step3-4.png';
import Step3Part5Image from './Step3-5.png';
import Step4Image from './Step4.png';

const Gradle: FunctionComponent = () => {
  const handleDownloadBitriseAddonsConfig = useDownloadConfigAndroid();

  return (
    <Guide data-trace-at-id={dataAtIds.GettingStartedGuideAndroidGradle} name="gradle">
      <GuideStep heading="Sample app" number="1">
        <Text>
          If you&apos;re not using your own app, start off by cloning a Simple Android Test app in
          your terminal.
        </Text>
        <GuideCode bash copy>
          git clone git@github.com:bitrise-io/sunflower.git
        </GuideCode>
      </GuideStep>

      <GuideStep heading="Download Config File" number="2">
        <Text>
          Now we are going to add the{' '}
          <Text inline monospace weight="bold">
            bitrise-addons-configuration.json
          </Text>{' '}
          to your project
        </Text>
        <Flex alignChildren="middle" direction="horizontal" gap="x3">
          <Flex grow initial="none">
            <Text>
              1: Download{' '}
              <Text inline weight="bold">
                bitrise-addons-configuration.json
              </Text>
            </Text>
          </Flex>

          <Flex>
            <Buttons>
              <Button elevation="x1" onClick={handleDownloadBitriseAddonsConfig} size="small">
                <Icon name="Download" size="1rem" />
                <Text>Download</Text>
              </Button>
            </Buttons>
          </Flex>
        </Flex>
        <Text>2. Open your project in Android Studio</Text>
        <GuideCode bash copy>
          studio ./sunflower
        </GuideCode>
        <Text>
          3: Now, drag and drop the{' '}
          <Text inline weight="bold">
            bitrise-addons-configuration.json
          </Text>{' '}
          to the root of your project
        </Text>
        <Image src={Step2Part3Image} borderRadius="x2" elevation="x1" width="27.75rem" />
      </GuideStep>

      <GuideStep heading="Initialize SDK" number="3">
        <Text>Still in Android Studio,</Text>
        <Text>
          1. Open the{' '}
          <Text inline monospace weight="bold">
            build.gradle
          </Text>{' '}
          file in the{' '}
          <Text inline weight="bold">
            root of your project
          </Text>
        </Text>
        <Text>
          2. Locate the{' '}
          <Text inline monospace weight="bold">
            dependencies
          </Text>{' '}
          block inside the{' '}
          <Text inline monospace weight="bold">
            buildscript
          </Text>{' '}
          block, add the following:
        </Text>
        <GuideCode copy>
          classpath &apos;io.bitrise.trace.plugin:trace-gradle-plugin:[latestVersion]&apos;
        </GuideCode>
        <Ribbon type="inform">
          <Flex direction="horizontal" gap="x4">
            <Flex>
              <Icon name="Info" />
            </Flex>
            <Flex grow initial="none">
              <Text>
                Note, we’ve used{' '}
                <Text inline monospace weight="bold">
                  [latestVersion]
                </Text>{' '}
                here as a placeholder. When you paste into Android Studio, it should highlight the
                line as a warning, and suggest that you use the latest version (e.g. 0.0.2). If in
                doubt please check{' '}
                <Link
                  href="https://repo1.maven.org/maven2/io/bitrise/trace/plugin/trace-gradle-plugin/"
                  target="__blank"
                  rel="noopener noreferrer"
                >
                  https://repo1.maven.org/maven2/io/bitrise/trace/plugin/trace-gradle-plugin/
                </Link>{' '}
                for the latest released version.
              </Text>
            </Flex>
          </Flex>
        </Ribbon>
        <Image src={Step3Part2Image} borderRadius="x2" elevation="x1" width="37.125rem" />
        <Text>
          3. Open the{' '}
          <Text inline monospace weight="bold">
            build.gradle
          </Text>{' '}
          file in your{' '}
          <Text inline weight="bold">
            application module
          </Text>
        </Text>
        <Text>
          4. Add the following after the other{' '}
          <Text inline monospace weight="bold">
            apply plugin
          </Text>{' '}
          statements:
        </Text>
        <GuideCode copy>apply plugin: &apos;io.bitrise.trace.plugin&apos;</GuideCode>
        <Image src={Step3Part4Image} borderRadius="x2" elevation="x1" width="37.125rem" />
        <Text>
          5. Staying in the{' '}
          <Text inline weight="bold">
            application module
          </Text>{' '}
          <Text inline monospace weight="bold">
            build.gradle
          </Text>
          , add the following dependency:
        </Text>
        <GuideCode copy>
          implementation &apos;io.bitrise.trace:trace-sdk:[latestVersion]&apos;
        </GuideCode>
        <Ribbon type="inform">
          <Flex direction="horizontal" gap="x4">
            <Flex>
              <Icon name="Info" />
            </Flex>
            <Flex grow initial="none">
              <Text>
                Note, we’ve used{' '}
                <Text inline monospace weight="bold">
                  [latestVersion]
                </Text>{' '}
                here as a placeholder. When you paste into Android Studio, it should highlight the
                line as a warning, and suggest that you use the latest version e.g. 0.0.3. If in
                doubt please check{' '}
                <Link
                  href="https://repo1.maven.org/maven2/io/bitrise/trace/trace-sdk/"
                  target="__blank"
                  rel="noopener noreferrer"
                >
                  https://repo1.maven.org/maven2/io/bitrise/trace/trace-sdk/
                </Link>{' '}
                for the latest released version.
              </Text>
            </Flex>
          </Flex>
        </Ribbon>
        <Image src={Step3Part5Image} borderRadius="x2" elevation="x1" width="37.125rem" />
        <Text>6. All Done!</Text>
      </GuideStep>

      <GuideStep heading="Now you can run it!" number="4">
        <Text>All that is left now is to build and run the app on a device.</Text>
        <Image borderRadius="x2" elevation="x1" src={Step4Image} width="600px" />
        <Text>1. Make sure you sync your project with the Gradle files.</Text>
        <Text>
          2. The first time you run the application, the build will take several minutes longer than
          normal while the Trace SDK applies the plugin.
        </Text>
        <Text>3. Check your logcat messages, Trace should now be working.</Text>
      </GuideStep>
    </Guide>
  );
};

export default Gradle;
